import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Eye, Pencil, Trash2 } from "lucide-react";
import { getQuotes, deleteQuote, getInvoices, deleteInvoice } from "@/lib/api";
import { fmtEur, fmtDate } from "@/lib/format";
import { useI18n } from "@/i18n";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const DocumentList = ({ kind }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const isQuote = kind === "quote";
  const base = isQuote ? "quotes" : "invoices";
  const [list, setList] = useState([]);
  const [toDelete, setToDelete] = useState(null);

  const load = () => (isQuote ? getQuotes() : getInvoices()).then(setList);
  useEffect(() => { load(); }, [kind]);

  const confirmDelete = async () => {
    await (isQuote ? deleteQuote(toDelete.id) : deleteInvoice(toDelete.id));
    setToDelete(null); load(); toast.success(t("common.delete"));
  };

  return (
    <div>
      <PageHeader title={t(isQuote ? "nav.quotes" : "nav.invoices")}>
        <Button data-testid={`new-${base}-button`} onClick={() => navigate(`/${base}/new`)} className="rounded-lg">
          <Plus className="mr-1.5 h-4 w-4" /> {t(isQuote ? "doc.quoteNew" : "doc.invoiceNew")}
        </Button>
      </PageHeader>

      {list.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">{t(isQuote ? "doc.emptyQuotes" : "doc.emptyInvoices")}</Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>{t("doc.number")}</TableHead>
                <TableHead>{t("doc.customer")}</TableHead>
                <TableHead className="hidden sm:table-cell">{t("common.date")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead className="text-right">{t("common.total")}</TableHead>
                <TableHead className="w-32 text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((d, i) => (
                <motion.tr key={d.id} data-testid={`${base}-row-${d.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="cursor-pointer border-b transition-colors hover:bg-slate-50" onClick={() => navigate(`/${base}/${d.id}`)}>
                  <TableCell className="font-semibold text-slate-900">{d.number}</TableCell>
                  <TableCell className="text-slate-700">{d.customer?.name}</TableCell>
                  <TableCell className="hidden text-slate-600 sm:table-cell">{fmtDate(d.issue_date)}</TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                  <TableCell className="text-right font-mono-num font-semibold text-slate-900">{fmtEur(d.total)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <button data-testid={`view-${base}-${d.id}`} onClick={() => navigate(`/${base}/${d.id}`)} className="rounded-md p-1.5 text-slate-500 hover:bg-accent hover:text-primary"><Eye className="h-4 w-4" /></button>
                      <button data-testid={`edit-${base}-${d.id}`} onClick={() => navigate(`/${base}/${d.id}/edit`)} className="rounded-md p-1.5 text-slate-500 hover:bg-accent hover:text-primary"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-${base}-${d.id}`} onClick={() => setToDelete(d)} className="rounded-md p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t("common.delete")}</AlertDialogTitle><AlertDialogDescription>{t("common.deleteConfirm")}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-document" className="bg-rose-600 hover:bg-rose-700">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DocumentList;
